// src/mixins/countEditCategoryMixin.js
// FT-192 (REQ-06): статья и подкатегория в диалоге правки платежа. Один код на
// журнал компании (CompanyCounts), кассу сотрудника (Counts) и диалоги правки в
// списке агентов (AgentsList) и в кассах сотрудников (admin/users/List).
//
// Компонент обязан иметь: editedCount (ответ GET /counts/:id), selected_count
// (значение v-combobox статьи) и translateCategory(value). Диалог открывается
// методом openEditCategory(count) — после того как editedCount пришёл с сервера.
//
// Что делает:
//   - список статей — по правилам формы нового платежа (без system, скрытых и
//     скрытых компанией) плюс текущая статья платежа;
//   - подкатегория — варианты выбранной статьи; скрытая текущая показана, но
//     другим платежам не предлагается; при смене статьи сбрасывается;
//   - в PATCH ключ counts_subcategory_id уходит, только если пользователь менял
//     поле (иначе сервер сохранит прежнее); у отменённого платежа поле закрыто.
import {
  categoriesWithCurrent,
  categoryToSend,
  companyCountCategories,
  hiddenCategoryCodes,
  isExpenseCategory,
  manualCategories,
  subcategoriesFor,
} from "@/utils/countCategory";

export default {
  data() {
    return {
      // id выбранной подкатегории, null — не выбрана
      selected_subcategory: null,
      // пользователь менял подкатегорию (или статью, что её сбрасывает)
      subcategory_changed: false,
      // статья, которой принадлежит выбранная подкатегория
      subcategory_owner: null,
    };
  },
  computed: {
    // Статьи компании как пришли с сервера; у филиала без строк — запасной список.
    editCategoriesSource() {
      const getters = (this.$store && this.$store.getters) || {};
      return companyCountCategories(getters.getCompany, { fallback: true });
    },
    editCategories() {
      const getters = (this.$store && this.$store.getters) || {};
      const manual = manualCategories(this.editCategoriesSource, {
        hiddenCodes: hiddenCategoryCodes(getters.getMainCompany),
      });
      return categoriesWithCurrent(manual, this.editCategoriesSource, this.editedCount && this.editedCount.group);
    },
    // Пункты v-combobox статьи: { text — подпись на языке интерфейса, value — Count.group }.
    editGroupItems() {
      return this.editCategories.map((category) => ({ text: this.translateCategory(category.value), value: category.value }));
    },
    // Статья, которая уйдёт в PATCH (набранный руками текст вне списка не в счёт;
    // допустимые — пункты списка диалога, без system и скрытых).
    editCategoryValue() {
      return categoryToSend(
        this.editedCount || {},
        this.selected_count,
        this.editGroupItems.map((item) => item.value)
      );
    },
    editSubcategoryOptions() {
      const category = this.editCategories.find((item) => item.value === this.editCategoryValue);
      const count = this.editedCount || {};
      return subcategoriesFor(category, { includeInactiveId: count.counts_subcategory_id });
    },
    showEditSubcategory() {
      return this.editSubcategoryOptions.length > 0;
    },
    // Отменённый платёж (оригинал) подкатегорию не меняет; у сторно сервер ответит 422 —
    // клиент его не отличает надёжно (описание «Корректировка…» не признак).
    editSubcategoryLocked() {
      return !!(this.editedCount && this.editedCount.canceled);
    },
    // Статья закрыта: у проведённого платежа (completed) и у отменённого с подкатегорией
    // (NEG-04: смена статьи сняла бы подкатегорию у одной половины пары — сервер ответит 422).
    // Сторно клиент не распознаёт — его 422 покажется обычной ошибкой сохранения.
    editCategoryLocked() {
      const count = this.editedCount || {};
      if (count.completed === true) return true;
      const subcategoryId = count.counts_subcategory_id;
      return !!count.canceled && subcategoryId !== undefined && subcategoryId !== null;
    },
    // Доход (true) или расход (false), однозначно заданный статьёй диалога; null — статья типа не задаёт
    // («Залог», переводы, старые статьи компаний без однозначного типа).
    editCategoryOperation() {
      const category = this.editCategoriesSource.find((item) => item && item.value === this.editCategoryValue);
      const expense = category ? isExpenseCategory(category) : null;
      return expense === null ? null : !expense;
    },
    // Переключатель «Доходы/Расходы» диалога закрыт: проведённый платёж тип не меняет (сервер — 422),
    // отменённый — тоже (сторно — копия с тем же типом: пара перестала бы гаситься), а однозначный тип задаёт статья.
    editOperationLocked() {
      const count = this.editedCount || {};
      return count.completed === true || !!count.canceled || this.editCategoryOperation !== null;
    },
    // Подсказка под закрытой статьёй — причина блокировки.
    editCategoryLockHint() {
      const count = this.editedCount || {};
      if (count.completed === true) return this.$t("custom.count_category_locked");
      return this.editCategoryLocked ? this.$t("custom.count_category_locked_canceled") : "";
    },
  },
  methods: {
    // Открытие диалога правки: в поле статьи — статья платежа, подкатегория — как есть.
    openEditCategory(count) {
      this.selected_count = { text: this.translateCategory(count.group), value: count.group };
      this.resetEditSubcategory(count);
    },
    // Подкатегория платежа как есть, «менял» — нет.
    resetEditSubcategory(count) {
      const id = count ? count.counts_subcategory_id : null;
      this.selected_subcategory = id === undefined ? null : id;
      this.subcategory_changed = false;
      this.subcategory_owner = count ? count.group : null;
    },
    // Пользователь выбрал другую статью: тип платежа — по статье, если она его задаёт (как в форме
    // нового платежа), кроме проведённого и отменённого (у отменённого сторно — копия с тем же типом,
    // смена типа одной половины разорвала бы пару); подкатегория старой статьи сбрасывается.
    // Действует во всех диалогах на миксине: CompanyCounts, Counts, AgentsList, admin/users/List.
    onEditCategoryChange() {
      const value = this.editCategoryValue;
      const operation = this.editCategoryOperation;
      const count = this.editedCount;
      if (operation !== null && count && count.completed !== true && !count.canceled) {
        count.operation = operation;
      }
      if (value === this.subcategory_owner) return;
      this.selected_subcategory = null;
      this.subcategory_changed = true;
      this.subcategory_owner = value;
    },
    onEditSubcategoryInput() {
      this.subcategory_changed = true;
    },
    // Часть тела PATCH: ключ есть, только если поле менялось (и платёж не отменён).
    editSubcategoryPayload() {
      if (!this.subcategory_changed || this.editSubcategoryLocked) return {};
      const id = this.selected_subcategory;
      return { counts_subcategory_id: id === undefined ? null : id };
    },
  },
};
